// Turns a clipboard payload into table markup.
//
// Pasting from a spreadsheet is the PRIMARY way tables are expected to be
// created — the data almost always already exists somewhere else — so this path
// gets more care than the empty-grid path.
//
// Cells are imported as TEXT. Excel and Google Sheets put a wall of inline
// `<font>`, `background`, and `mso-*` styling on the clipboard, and letting any
// of it through would drag a foreign visual style into the chapter. The
// instructor formats in Kudu afterwards.

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function normalizeCellText(value: string) {
  // Collapse the newlines and runs of whitespace a spreadsheet emits inside a
  // single cell; a cell is a value, not a paragraph.
  return value.replace(/\s+/g, ' ').trim();
}

function padRows(rows: string[][]): string[][] {
  const width = rows.reduce((widest, row) => Math.max(widest, row.length), 0);

  // A ragged paste (merged cells in the source, or a trailing short row) would
  // otherwise produce a table with holes in it.
  return rows.map((row) => {
    const padded = [...row];

    while (padded.length < width) {
      padded.push('');
    }

    return padded;
  });
}

function parseHtmlTable(html: string): string[][] | null {
  if (typeof DOMParser === 'undefined' || !/<table[\s>]/i.test(html)) {
    return null;
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const table = doc.body.querySelector('table') as HTMLTableElement | null;

  if (!table) {
    return null;
  }

  const rows = Array.from(table.rows).map((row) =>
    Array.from(row.cells).map((cell) =>
      normalizeCellText(cell.textContent ?? ''),
    ),
  );

  return rows.length > 0 ? padRows(rows) : null;
}

function parseDelimitedText(text: string): string[][] | null {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');

  // Drop a trailing blank line: copying a range usually ends with one.
  while (lines.length > 0 && lines[lines.length - 1].trim() === '') {
    lines.pop();
  }

  if (lines.length === 0) {
    return null;
  }

  // Tab separated only. Commas are far too common inside ordinary prose to
  // treat as a column boundary — "Paste this, then that" is not a two-column
  // table.
  if (!lines.some((line) => line.includes('\t'))) {
    return null;
  }

  const rows = lines.map((line) =>
    line.split('\t').map((cell) => normalizeCellText(cell)),
  );

  return padRows(rows);
}

/**
 * Parses a clipboard payload into a grid of cell text, or null when it is not
 * tabular.
 */
export function parsePastedTable(
  html: string,
  text: string,
): string[][] | null {
  const grid = parseHtmlTable(html) ?? parseDelimitedText(text);

  if (!grid || grid.length === 0 || grid[0].length === 0) {
    return null;
  }

  return grid;
}

/**
 * Reports whether a grid is worth turning into a table.
 *
 * A single cell is NOT: copying one cell out of Excel still puts a one-cell
 * `<table>` on the clipboard, and replacing the instructor's whole table because
 * they pasted one value would be maddening.
 */
export function isMultiCellGrid(grid: string[][]): boolean {
  return grid.length > 1 || (grid[0]?.length ?? 0) > 1;
}

/**
 * Builds table markup from a grid, with the first row as a header.
 */
export function buildTableHtmlFromGrid(grid: string[][]): string {
  const [headerCells = [], ...bodyRows] = grid;

  const head = `<thead><tr>${headerCells
    .map((cell) => `<th scope="col"><p>${escapeHtml(cell)}</p></th>`)
    .join('')}</tr></thead>`;

  const body = `<tbody>${bodyRows
    .map(
      (row) =>
        `<tr>${row.map((cell) => `<td><p>${escapeHtml(cell)}</p></td>`).join('')}</tr>`,
    )
    .join('')}</tbody>`;

  return `<table>${head}${bodyRows.length > 0 ? body : ''}</table>`;
}
